import React from 'react'
import { Flex, Text } from '@chilipiper/design-system'
import { Avatar } from '@chilipiper/design-system/src/new'
import { useMessageFeedContext } from '../../chat-widget/MessageFeedContext'
import { LiveMessageBlock } from '../../chat-data/types'
import { getName } from '../../helpers/getName'
import { usePersonalDetails } from '../../api-queries/personal-details'
import { BlockOrCompositeBlock } from '../compositeBlocks'
import { ReplyButtonsBlock } from './reply-buttons-block/ReplyButtonsBlock'
import { MessageBlock } from './message-block/MessageBlock'
import { FillFormBlock } from './fill-form-block/FillFormBlock'
import { SuggestedTimes } from './book-meeting-block/SuggestedTimes'
import { BookMeetingBlock } from './book-meeting-block/BookMeetingBlock'

interface Props {
  block: BlockOrCompositeBlock
  isOwnMessage: boolean
  onLiveMessageVisible: (event: LiveMessageBlock) => void
}

export const BlockRender = ({ block, isOwnMessage, onLiveMessageVisible }: Props) => {
  const { pickReplyButton } = useMessageFeedContext()

  switch (block.type) {
    case 'Message':
    case 'PiperAiMessage':
      return <MessageBlock block={block} isOwnMessage={isOwnMessage} />
    case 'LiveMessage':
      return (
        <MessageBlock
          block={block}
          isOwnMessage={isOwnMessage}
          onMessageVisible={onLiveMessageVisible}
        />
      )
    case 'MessageToPiperAi':
      return (
        <MessageBlock
          block={{
            ...block,
            parts: [{ type: 'Text', text: block.value.content }],
            type: 'LiveMessage',
          }}
          isOwnMessage={isOwnMessage}
        />
      )
    case 'ReplyButtons':
      return <ReplyButtonsBlock block={block} onReplyButtonClick={pickReplyButton} />
    case 'FillForm':
      return <FillFormBlock block={block} />
    case 'BookMeeting':
      return <BookMeetingBlock block={block} />
    case 'SuggestedTimes':
      return <SuggestedTimes block={block} />
    case 'SystemMessage':
      if (block.payload.type === 'SystemConversationAssigned') {
        return <AssignmentBlock />
      }
      return <>Unimplemented block</>

    case 'SystemUserResponded':
      return <SystemUserRespondedBlock block={block} />
    case 'SystemChatTransferred':
      return <SystemChatTransferredBlock />
    default:
      return <>Unimplemented block</>
  }
}

const AssignmentBlock = () => {
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        Waiting for someone to join
      </Text>
    </Flex>
  )
}

const SystemUserRespondedBlock = ({
  block,
}: {
  block: Extract<BlockOrCompositeBlock, { type: 'SystemUserResponded' }>
}) => {
  const { tenantId } = useMessageFeedContext()
  const { data: target } = usePersonalDetails(tenantId, block.author)
  const targetDisplayName = getName(target) ?? '...'
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Avatar id={block.author} name={targetDisplayName} image={target?.imageUrl} size={5} />
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        {targetDisplayName} joined the chat
      </Text>
    </Flex>
  )
}

const SystemChatTransferredBlock = () => {
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        Chat is being transferred
      </Text>
    </Flex>
  )
}
